import clsx from 'clsx';
import React, {
  FunctionComponent,
  MutableRefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { AttributionControl, Map, WMSTileLayer, Pane } from 'react-leaflet';
import useMediaQuery from 'use-media-query-hook';
import { orAbove } from '../../common/breakpointQueries';
import { useBoundsAndZoom } from '../../services/hooks/useBoundsAndZoom';
import { Capabilities } from '../../services/hooks/useCapabilities';
import {
  EPSG900913,
  LAYERS,
  MAX_ZOOM,
  MIN_ZOOM,
  NORDIC_BOUNDS,
  SEAICE_CENTER,
} from './MapConfig';
import { MapControls } from './MapControls';
import styles from './MapViewer.scss';
import { StepControls } from './StepControls';
import { TimedGroup } from './TimedGroup';
import { TimedLayer } from './TimedLayer';
import { useMapFullscreen } from './useMapFullscreen';

export interface SatelliteMapProps {
  mapRef: MutableRefObject<Map | null>;
  layerGroups: TimedGroup[];
  times: string[] | undefined;
  layerGroup: TimedGroup;
  setLayerGroup: (val: TimedGroup) => void;
  timeIndex: number | null;
  setTimeIndex: (val: number) => void;
  capabilities: Capabilities;
}

export const SatelliteMap: FunctionComponent<SatelliteMapProps> = ({
  mapRef,
  layerGroups,
  times,
  layerGroup,
  setLayerGroup,
  timeIndex,
  setTimeIndex,
  capabilities,
}) => {
  const [mapComponent, setMapComponent] = useState<Map | null>(null);
  // synchronize external ref with map component
  useEffect(() => {
    if (!mapRef) return () => {};
    mapRef.current = mapComponent;
    return () => (mapRef.current = null);
  }, [mapRef, mapComponent]);
  const map = mapComponent?.leafletElement;

  const wideDevice = useMediaQuery(orAbove.s);
  const tallDevice = useMediaQuery('all and (min-height: 32em)');
  const [hasTap, setTap] = useState(wideDevice);
  const { minimizeMap, maximizeMap, element, isFullscreen } = useMapFullscreen(
    map
  );
  const mapControlsLayout =
    !isFullscreen || tallDevice ? 'tall' : wideDevice ? 'wide' : 'tiny';

  const mustToggleTap = !isFullscreen && !(tallDevice && wideDevice);
  // disables panning and scrolling for small devices unless hasTap is set
  // also reenables it if device becomes larger
  useEffect(() => {
    if (!map) return () => {};
    if (hasTap || !mustToggleTap) {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      if (map.tap) map.tap.enable();
    } else {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      if (map.tap) map.tap.disable();
    }

    return () => {};
  }, [hasTap, mustToggleTap, map]);

  const { zoom } = useBoundsAndZoom(map);

  const zoomIn = useCallback(() => map?.zoomIn(), [map]);

  const zoomOut = useCallback(() => map?.zoomOut(), [map]);

  const backgroundLayers = [
    LAYERS.OSM_BACKGROUND_SWEDEN,
  ].map((layer, index) => (
    <WMSTileLayer
      key={layer.key}
      {...layer.server}
      layers={layer.key}
      format="image/png"
      transparent={true}
      tileSize={256}
      zIndex={index}
      attribution={layer.attribution}
    />
  ));

  const foregroundLayers = [
    LAYERS.OSM_ROADS_SWEDEN,
    LAYERS.OSM_LABELS_SWEDEN,
  ].map((layer, index) => (
    <WMSTileLayer
      key={layer.key}
      {...layer.server}
      layers={layer.key}
      format="image/png"
      transparent={true}
      tileSize={256}
      zIndex={backgroundLayers.length + 2 + index}
      attribution={layer.attribution}
    />
  ));

  return (
    <>
      <div className={styles.mapViewerContainer}>
        <div
          className={clsx(
            styles.mapViewer,
            isFullscreen && styles.fakeFullscreen
          )}
          ref={element}
        >
          <Map
            center={SEAICE_CENTER}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            zoom={MIN_ZOOM}
            ref={setMapComponent}
            zoomControl={false}
            maxBounds={NORDIC_BOUNDS}
            maximumAge={1000 * 60 * 60}
            crs={EPSG900913}
            fadeAnimation={false}
            attributionControl={false}
          >
            <AttributionControl position="bottomright" prefix={false} />
            <Pane>{backgroundLayers}</Pane>
            <Pane>
              {layerGroup.layers.map((timedLayer, index) => {
                const capability = capabilities[timedLayer.key];
                if (!capability) return null;
                if (!times) return null;

                return (
                  <TimedLayer
                    key={timedLayer.key}
                    server={timedLayer.server}
                    capability={capability}
                    desiredTime={times[timeIndex || 0]}
                    zIndex={2 + index}
                  />
                );
              })}
            </Pane>
            <Pane>{foregroundLayers}</Pane>
            <MapControls
              stepControlFormat="dddd DD/M HH:mm"
              setLayer={setLayerGroup}
              currentLayer={layerGroup}
              layers={layerGroups}
              maxZoom={MAX_ZOOM}
              minZoom={MIN_ZOOM}
              currentTimeIndex={timeIndex || 0}
              times={times || []}
              setCurrentTimeIndex={setTimeIndex}
              currentZoom={zoom ? zoom : MIN_ZOOM}
              isFullscreen={isFullscreen}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              minimizeMap={minimizeMap}
              maximizeMap={maximizeMap}
              hasTap={hasTap}
              setTap={setTap}
              mustToggleTap={mustToggleTap}
              layout={mapControlsLayout}
            />
          </Map>
        </div>
        <StepControls
          format="dddd DD/M HH:mm"
          variant="secondary"
          times={times || []}
          setCurrentTimeIndex={setTimeIndex}
          currentTimeIndex={timeIndex || 0}
        />
      </div>
    </>
  );
};
